import React, { useState, useEffect } from 'react';
import * as authActions from '../../../../app/src/actions/authActions';
import PropTypes from 'prop-types';
import useEvent from 'react-use/lib/useEvent';
import useInterval from 'react-use/lib/useInterval';

import { useHistory, Redirect } from 'react-router-dom';
import { throttle } from 'Utils/lodashUtils';
import useAlerts from '../alerts';
import Button from 'Components/Button/Button';
import counterpart from 'counterpart';
import PrimaryButton from 'Components/Button/PrimaryButton';
import useSession from 'Components/session/session';
import './SessionMonitor.scss';
import { eventEmitter} from 'Components/session/gpAuthService';
import { resetInactivityTimer } from '../gpAuthService';

const SessionMonitor = (props) => {
    const delay = 1000;
    const [responseTimer, setResponseTimer] = useState(props.warningResponseTimeoutSeconds);
    const [isRunning, setIsRunning] = useState(false);
    const [isRenewing, setIsRenewing] = useState(false);
    const [showTimerModal, setShowTimerModal] = useState(false);
    const session = useSession();
    const history = useHistory();
    const [autoLogout, setAutoLogout] = useState(false);
    const { postAlert } = useAlerts();
    const [needsRenewal, setNeedsRenewal] = useState(true);
    const [lastRenewalTime, setLastRenewalTime] = useState(null);
  
     // Listen to GP-Auth timer events
    useEffect(() => {
        const handleTimerChange = ({ timerIsRunning, countdownSeconds: seconds }) => { setIsRunning(timerIsRunning);
            setResponseTimer(seconds);

            // Show modal only when timer is actively running (user is inactive) and countdown is in warning threshold
            if (timerIsRunning && seconds > 0 && seconds <= props.warningResponseTimeoutSeconds) {
                setShowTimerModal(true);
            } else {
                setShowTimerModal(false);
            }
        };


        eventEmitter.on('OnTimerChange', handleTimerChange);

        return () => {
            eventEmitter.off('OnTimerChange', handleTimerChange);
        } ;
    }, [props.warningResponseTimeoutSeconds]);
    const renew = () => {
      setShowTimerModal(false); // Explicitly hide modal
      setResponseTimer(props.warningResponseTimeoutSeconds);
      setIsRunning(false);
      setIsRenewing(true);
      setLastRenewalTime(Date.now());
      resetInactivityTimer(); // Reset the GP Auth timer
      session.renew().then((newSessionResponse) => {
        if(JSON.parse(sessionStorage.getItem("state")).auth.accessToken !== newSessionResponse.token){
          props.authActions.setAuthToken(newSessionResponse.token);
          props.authActions.updateTokenExpirationTime(newSessionResponse.expiry);
        }
        setIsRenewing(false);
        //No need to alert user about refreshed session, as it is done silently using MSAL
        //postAlert();
      });
    };

    // GP Auth handles countdown - removed local useInterval
  
    const logout = () => {
      setIsRunning(false);
      setAutoLogout(true);
    };
  
  
    useEffect(() => {
      if (autoLogout) {
        history.push('/logout');
      }
    }, [autoLogout, history]);


    useEffect(() => {
      if (!lastRenewalTime) {
          setLastRenewalTime(Date.now());
      }
    }, [lastRenewalTime]);
  
    useEffect(() => {
      // GP Auth handles idle detection - check if timeout reached
      if (isRunning && responseTimer === 0) {
        logout();
      }
    }, [isRunning, responseTimer]);

    // GP Auth handles cross-tab sync - removed otherTabActive check
  
    const showTimer = () => {
      return (
        <div id="sessionTimer" className="dims-session-timeout-wrapper">
          <div id="sessionTimer1" className="dims-session-timeout">
            <div id="sessionTimerheader" className="dims-session-timeout-header">
              <h1> {counterpart('globalTranslate.sessionTimeout.header')}</h1>
            </div>
            <div id="sessionTimercontent" className="dims-session-timeout-content">
              {counterpart('globalTranslate.sessionTimeout.content')}
            </div>
            <div id="sessionTimerfooter" className={`dims-session-timeout-footer dims-session-timeout-footer` + (props.auth.user.languageAbbr === 'FC' ?  `-fr` : `-en`)}>
              <Button
                id="session_timeout_logout_btn"
                class="button button-hollow current"
                action={logout}
              >
                {counterpart('globalTranslate.siteHeader.logout')}
              </Button>
              <PrimaryButton
                action={renew}
                id="session_timeout_stay_connected_btn"
              >
                {`${counterpart('globalTranslate.sessionTimeout.stayConnected')}(${
                  responseTimer
                  })`}
              </PrimaryButton>
            </div>
          </div>
        </div>
      );
    };
  
    if (session.isAuthenticated()) {
      return showTimerModal ? showTimer() : <div />;
    } else {
      return <Redirect to="/login" />;
    }
  };
  
  SessionMonitor.propTypes = {
    warningResponseTimeoutSeconds: PropTypes.number,
    sessionInactivityTimeoutSeconds: PropTypes.number,
    renewThresholdSeconds: PropTypes.number,
  };
  
  SessionMonitor.defaultProps = {
    warningResponseTimeoutSeconds: 60,
     // 14 minutes + 1 minute countdown for a total of 15 minute inactivity timeout
    sessionInactivityTimeoutSeconds: 14 * 60,
    renewThresholdSeconds: 45 * 60,
  };
  
  export default SessionMonitor;
  